import { useEffect, useMemo, useRef, useState } from 'react';

import { useAppDispatch, useAppSelector } from '@src/core/hooks';
import { loadAvailableDates, type ProductScheduleState } from '@src/stores/product';
import { MonthTuple, changeMonth, initialMonth } from '@src/common/calendar';
import { computeVisibleRange } from '@src/domains/scheduling/core/computeVisibleRange';
import { shouldAdvanceAutoLoad } from '@src/domains/scheduling/core/shouldAdvanceAutoLoad';
import { intersectPackageAvailableDates, type PackageProductDates } from '@src/domains/productPackage';

const AUTO_LOAD_LIMIT = 5;

const EMPTY_PRODUCT_SCHEDULE: ProductScheduleState = { dates: {}, loadedKeys: {}, pending: true };

type UsePackageScheduleIntersectionParams = {
  scheduledProductIds: string[];
  enabled?: boolean;
};

type UsePackageScheduleIntersectionReturn = {
  monthTuple: MonthTuple;
  setMonthTuple: (next: MonthTuple) => void;
  dates: PackageProductDates;
  pending: boolean;
};

const dateIsInRange = (date: string, dateFrom: string, dateTo: string): boolean => date >= dateFrom && date <= dateTo;

export function usePackageScheduleIntersection({
  scheduledProductIds,
  enabled = true,
}: UsePackageScheduleIntersectionParams): UsePackageScheduleIntersectionReturn {
  const dispatch = useAppDispatch();
  // Select the raw slice (stable reference unless a schedule actually changes) and derive the
  // per-product list in useMemo — mapping straight out of useAppSelector returned a new array
  // (and new default schedule objects) on every dispatch, forcing this hook's consumers to
  // re-render on unrelated state changes.
  const productSchedules = useAppSelector((state) => state.product.productSchedules);

  const [monthTuple, setMonthTuple] = useState<MonthTuple>(initialMonth);
  const autoLoadCountRef = useRef(AUTO_LOAD_LIMIT);
  const monthTupleRef = useRef(monthTuple);
  monthTupleRef.current = monthTuple;

  const productIdsKey = scheduledProductIds.join(',');
  const schedules = useMemo(
    () =>
      scheduledProductIds.map((productId) => ({
        productId,
        schedule: productSchedules[productId] ?? EMPTY_PRODUCT_SCHEDULE,
      })),
    // scheduledProductIds is tracked via productIdsKey since a fresh array reference from the
    // caller shouldn't invalidate this memo when its contents didn't change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [productSchedules, productIdsKey],
  );
  const prevProductIdsKeyRef = useRef(productIdsKey);
  const skipAutoLoadRef = useRef(false);

  useEffect(() => {
    const productIdsChanged = prevProductIdsKeyRef.current !== productIdsKey;
    prevProductIdsKeyRef.current = productIdsKey;

    if (!enabled || !scheduledProductIds.length) return;

    if (productIdsChanged) {
      const nextInitial = initialMonth();
      setMonthTuple(nextInitial);
      autoLoadCountRef.current = AUTO_LOAD_LIMIT;
      skipAutoLoadRef.current = true;
      const range = computeVisibleRange(nextInitial);
      scheduledProductIds.forEach((productId) => {
        dispatch(loadAvailableDates(productId, range.dateFrom, range.dateTo));
      });
      return;
    }

    const range = computeVisibleRange(monthTuple);
    scheduledProductIds.forEach((productId) => {
      dispatch(loadAvailableDates(productId, range.dateFrom, range.dateTo));
    });
  }, [dispatch, enabled, monthTuple, productIdsKey, scheduledProductIds]);

  const datesByProductId = useMemo(
    () =>
      Object.fromEntries(schedules.map(({ productId, schedule }) => [productId, schedule.dates])) as Record<
        string,
        PackageProductDates
      >,
    [schedules],
  );

  const dates = useMemo(
    () => intersectPackageAvailableDates({ datesByProductId, scheduledProductIds }),
    [datesByProductId, scheduledProductIds],
  );

  const pending = schedules.some(({ schedule }) => schedule.pending);
  const range = computeVisibleRange(monthTuple);
  const rangeKey = [range.dateFrom, range.dateTo].join('-');
  const allLoaded = schedules.every(({ schedule }) => schedule.loadedKeys?.[rangeKey]);
  const hasDatesInVisibleMonth = Object.keys(dates).some((date) => dateIsInRange(date, range.dateFrom, range.dateTo));

  useEffect(() => {
    if (skipAutoLoadRef.current) {
      skipAutoLoadRef.current = false;
      return;
    }
    if (!allLoaded) return;

    const advance = shouldAdvanceAutoLoad({
      enableAutoLoad: enabled && Boolean(scheduledProductIds.length),
      remainingAttempts: autoLoadCountRef.current,
      pending,
      hasDates: hasDatesInVisibleMonth,
    });
    if (advance) {
      autoLoadCountRef.current -= 1;
      setMonthTuple(changeMonth(monthTupleRef.current, 1));
    }
  }, [allLoaded, enabled, hasDatesInVisibleMonth, pending, scheduledProductIds.length]);

  return { monthTuple, setMonthTuple, dates, pending };
}
